import React from 'react';

/** مسیر لوگوی برند برای لودر (public/assets) */
export const SITE_BRAND_LOADER_LOGO = '/assets/logo-mane-haghighi-asli-1 (1).png';

/** پس‌زمینهٔ لودر تمام‌صفحه؛ همرنگ پس‌زمینهٔ صفحهٔ اصلی سایت */
export const HOMEPAGE_LOADER_BG_CLASS =
  'bg-gradient-to-b from-[#f8fafc] via-[#eef2ff] to-[#f8fafc]';

const SIZE_MAP = {
  sm: {
    outer: 'h-[4.75rem] w-[4.75rem]',
    border: 'border-[3px]',
    pulseInset: 'inset-2',
    logo: 'h-8 max-h-[2rem] w-auto',
  },
  md: {
    outer: 'h-28 w-28',
    border: 'border-[3px]',
    pulseInset: 'inset-[9px]',
    logo: 'h-12 max-h-[3rem] w-auto',
  },
  lg: {
    outer: 'h-32 w-32',
    border: 'border-4',
    pulseInset: 'inset-3',
    logo: 'h-16 max-h-[4rem] w-auto',
  },
} as const;

export type SiteBrandedLoaderTone = 'light' | 'dark';

export interface SiteBrandedLoaderProps {
  message?: string;
  subtitle?: string;
  variant?: 'fullscreen' | 'section' | 'embedded';
  size?: keyof typeof SIZE_MAP;
  tone?: SiteBrandedLoaderTone;
  className?: string;
  innerClassName?: string;
}

const BrandOrbitCore: React.FC<{
  size: keyof typeof SIZE_MAP;
  tone: SiteBrandedLoaderTone;
  innerClassName?: string;
}> = ({ size, tone, innerClassName = '' }) => {
  const s = SIZE_MAP[size];
  const trackClass = tone === 'dark' ? 'border-[#1C3B9B]/25' : 'border-[#1C3B9B]/20';
  const pulseClass = tone === 'dark' ? 'from-[#1C3B9B]/14 to-[#C11277]/14' : 'from-[#1C3B9B]/10 to-[#C11277]/10';

  return (
    <div className={`relative flex ${s.outer} items-center justify-center ${innerClassName}`} aria-hidden>
      <div className={`absolute inset-0 rounded-full ${s.border} ${trackClass}`} />
      <div
        className={`absolute inset-0 animate-spin rounded-full ${s.border} border-transparent border-t-[#7c3aed] border-r-[#C11277]`}
      />
      <div className={`absolute ${s.pulseInset} animate-pulse rounded-full bg-gradient-to-br ${pulseClass}`} />
      <img
        src={SITE_BRAND_LOADER_LOGO}
        alt=""
        className={`relative z-10 ${s.logo} object-contain`}
        draggable={false}
      />
    </div>
  );
};

export const SiteBrandedLoader: React.FC<SiteBrandedLoaderProps> = ({
  message = 'در حال بارگذاری...',
  subtitle,
  variant = 'section',
  size = 'md',
  tone = 'light',
  className = '',
  innerClassName = '',
}) => {
  const fullscreenSameAsHome = variant === 'fullscreen';
  const surfaceTone = fullscreenSameAsHome ? 'light' : tone;

  const textMain =
    surfaceTone === 'dark'
      ? 'text-sm font-semibold tracking-wide text-white/85'
      : 'text-sm font-semibold tracking-wide text-[#1e293b]';
  const textSub =
    surfaceTone === 'dark' ? 'text-xs text-white/55' : 'text-xs text-[#475569]/90';

  const core = (
    <div className="flex flex-col items-center gap-4 sm:gap-5">
      <BrandOrbitCore size={size} tone={surfaceTone} innerClassName={innerClassName} />
      {variant !== 'embedded' && (message || subtitle) ? (
        <div className="flex max-w-[min(92vw,26rem)] flex-col gap-1 px-3 text-center">
          {message ? <p className={textMain}>{message}</p> : null}
          {subtitle ? <p className={textSub}>{subtitle}</p> : null}
        </div>
      ) : null}
    </div>
  );

  if (variant === 'fullscreen') {
    return (
      <div
        role="status"
        aria-busy="true"
        aria-live="polite"
        className={`fixed inset-0 z-[100] flex min-h-[100dvh] items-center justify-center ${HOMEPAGE_LOADER_BG_CLASS} ${className}`}
      >
        {core}
      </div>
    );
  }

  if (variant === 'embedded') {
    return (
      <div role="status" aria-busy="true" className={`inline-flex ${className}`}>
        <BrandOrbitCore size={size} tone={tone} innerClassName={innerClassName} />
      </div>
    );
  }

  return (
    <div
      role="status"
      aria-busy="true"
      aria-live="polite"
      className={
        tone === 'dark'
          ? `flex min-h-[8rem] w-full flex-col items-center justify-center bg-[#0a0a0a] px-4 py-12 sm:min-h-[12rem] sm:py-14 ${className}`
          : `flex min-h-[8rem] w-full flex-col items-center justify-center bg-gradient-to-b from-[#f8fafc]/95 via-[#eef2ff]/90 to-[#f8fafc]/95 px-4 py-12 sm:min-h-[12rem] sm:py-14 ${className}`
      }
    >
      {core}
    </div>
  );
};

export interface BrandSpinnerRingProps {
  className?: string;
  size?: 'xs' | 'sm' | 'md';
  inverse?: boolean;
}

/** حلقهٔ چرخان هم‌رنگ orbit؛ برای دکمه و فضای کوچک */
export function BrandSpinnerRing({
  className = '',
  size = 'sm',
  inverse = false,
}: BrandSpinnerRingProps) {
  const dim =
    size === 'xs'
      ? 'h-3.5 w-3.5 border-[1.5px]'
      : size === 'md'
        ? 'h-6 w-6 border-[2.5px]'
        : 'h-4 w-4 border-2';

  const colors = inverse
    ? 'border-white/25 border-t-white border-r-white border-b-white/25 border-l-white/25'
    : 'border-[#1C3B9B]/22 border-t-[#7c3aed] border-r-[#C11277] border-b-[#1C3B9B]/15 border-l-[#1C3B9B]/15';

  return (
    <span
      className={`inline-block shrink-0 animate-spin rounded-full ${dim} ${colors} ${className}`}
      role="status"
      aria-busy="true"
      aria-label="در حال بارگذاری"
    />
  );
}

export default SiteBrandedLoader;
